import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../../services/api";
import { toast } from "react-toastify";

const postStatus = async (id, status, rejectReason) => {
  const payload = {
    status,
    rejectReason,
    rejectionReason: rejectReason,
    reason: rejectReason,
  };

  try {
    const res = await api.post(`/application-image/status/${id}`, payload);
    return res.data;
  } catch (error) {
    if (error.response?.status !== 404) throw error;
  }

  if (status === "approved") {
    const res = await api.post(`/application-image/approve/${id}`);
    return res.data;
  }

  if (status === "pending") {
    const res = await api.post(`/application-image/pending/${id}`);
    return res.data;
  }

  if (status === "rejected") {
    const res = await api.post(`/application-image/reject/${id}`, payload);
    return res.data;
  }

  throw new Error("Invalid status");
};

export const useUpdateApplicationImageStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, status, rejectReason }) => {
      const normalized = String(status || "").toLowerCase();

      if (normalized === "rejected") {
        const trimmed = String(rejectReason || "").trim();
        if (!trimmed) {
          throw new Error("Rejection reason is required.");
        }
        return postStatus(id, "rejected", trimmed);
      }

      if (normalized === "approved" || normalized === "pending") {
        return postStatus(id, normalized);
      }

      throw new Error("Invalid status");
    },
    onSuccess: (_, { status }) => {
      const label = String(status || "").toLowerCase();
      toast.success(
        label === "pending"
          ? "Status set to pending. You can approve another image within your plan limit."
          : `Status updated to ${label}`,
      );
      queryClient.invalidateQueries({ queryKey: ["viewApplicationImages"] });
      queryClient.invalidateQueries({ queryKey: ["applicationImage"] });
      queryClient.invalidateQueries({
        queryKey: ["viewSubscription"],
        refetchType: "all",
      });
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to update status"
      );
    },
  });
};
